import {
  ApiOnboardingStep,
  ApiACLRole,
  ApiStatus,
  ApiOnboardingType,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import StandardOnboardingFlow from "features/onboarding/components/StandardOnboardingFlow";
import {
  deserializeEcomPlatformOnboardingStep,
  getEcomPlatformConfig,
  getEcomPlatformNextOnboardingStep,
  getEcomPlatformPrevOnboardingStep,
  isEcomPlatformOnboardingType,
  normalizeEcomPlatformStep,
  serializeEcomPlatformOnboardingStep,
} from "features/onboarding/ecomPlatform/constants";
import EcomPlatformOnboardingFlow from "features/onboarding/ecomPlatform/EcomPlatformOnboardingFlow";
import { useOnboarding } from "features/onboarding/hooks/useOnboarding";
import {
  publishOnboarding,
  updateOnboarding,
} from "features/onboarding/services/onboardingService";
import useProjectSettings from "features/project/hooks/useProjectSettings";
import { useSelectedProject } from "Hooks";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { useUser } from "Hooks/useUser";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { approvedMap } from "features/onboarding/constants";
import { getStandardPrevOnboardingStep } from "features/onboarding/utils";
import { useProjectUsers } from "features/users/hooks/userProjectUsers";
import { saveUser } from "features/users/services/userService";
import { useUsers } from "Hooks/useUsers";
import produce from "immer";

const ONBOARDING_COMPLETED = "AaXD8fM";

function GetStartedPage() {
  const { selectedProject, selectedClient } = useSelectedProject();
  const { hasPermission } = useUser();
  const { loading, onboarding, setOnboarding, fetched } = useOnboarding();
  const { setSettings, settingByType, hasProjectType } = useProjectSettings();
  const [learingShown, setLearingShown] = useState(onboarding.learningShown);
  const [onboardingCompletedRegistered, setOnboardingCompletedRegistered] =
    useState(false);

  const isEcomPlatformOnboarding = useMemo(
    () => isEcomPlatformOnboardingType(onboarding.onboardingType),
    [onboarding.onboardingType],
  );

  const ecomPlatformConfig = useMemo(
    () =>
      isEcomPlatformOnboarding && onboarding.onboardingType
        ? getEcomPlatformConfig(onboarding.onboardingType)
        : null,
    [isEcomPlatformOnboarding, onboarding.onboardingType],
  );

  const stepUrlOptions = useMemo(
    () => ({
      serialize: (value: ApiOnboardingStep | null) => {
        if (!value || !ecomPlatformConfig) {
          return value;
        }
        return serializeEcomPlatformOnboardingStep(ecomPlatformConfig, value);
      },
      deserialize: (value: string | null) => {
        if (!ecomPlatformConfig) {
          return ApiOnboardingStep.BASIC_INFO;
        }
        return (
          deserializeEcomPlatformOnboardingStep(ecomPlatformConfig, value) ??
          ecomPlatformConfig.firstStep
        );
      },
    }),
    [ecomPlatformConfig],
  );

  const defaultStep =
    ecomPlatformConfig?.firstStep ?? ApiOnboardingStep.BASIC_INFO;

  const [step, setStep] = useAppendSearchState<ApiOnboardingStep>(
    "step",
    defaultStep,
    isEcomPlatformOnboarding ? stepUrlOptions : undefined,
  );
  const history = useHistory();
  const allUsersData = useUsers();
  const { users, setUsers } = useProjectUsers();
  const inactiveUsers = useMemo(() => {
    return users.filter((user) => user.status === ApiStatus.INACTIVE);
  }, [users]);

  const uiStep = useMemo(() => {
    const fallback = defaultStep;
    if (hasPermission(ApiACLRole.SUPER_ADMIN)) {
      return onboarding.uiStepAdmin || fallback;
    }
    return onboarding.uiStep || fallback;
  }, [onboarding, hasPermission, defaultStep]);

  const normalizedStep = useMemo(() => {
    if (!ecomPlatformConfig || !step) {
      return step;
    }
    return normalizeEcomPlatformStep(ecomPlatformConfig, step);
  }, [ecomPlatformConfig, step]);

  useEffect(() => {
    const targetStep = ecomPlatformConfig
      ? normalizeEcomPlatformStep(
          ecomPlatformConfig,
          uiStep || ecomPlatformConfig.firstStep,
        )
      : uiStep || ApiOnboardingStep.BASIC_INFO;
    if (targetStep !== step) {
      setStep(targetStep);
    }
  }, [uiStep, step, setStep, ecomPlatformConfig]);

  const onStepChange = useCallback(
    async (nextStep, confirmedStep?) => {
      if (!selectedProject) {
        return;
      }
      let key = "uiStep";
      if (hasPermission(ApiACLRole.SUPER_ADMIN)) {
        key = "uiStepAdmin";
      }
      let payload = {
        ...onboarding,
        [key]: nextStep,
      };
      if (confirmedStep && !hasPermission(ApiACLRole.SUPER_ADMIN)) {
        payload = {
          ...payload,
          [approvedMap[confirmedStep]]: new Date(),
        };
      }
      const res = await updateOnboarding(selectedProject.id as number, payload);
      setOnboarding(res);
    },
    [selectedProject, onboarding, setOnboarding, hasPermission],
  );

  const onBack = useCallback(() => {
    if (ecomPlatformConfig && normalizedStep) {
      const prev = getEcomPlatformPrevOnboardingStep(
        ecomPlatformConfig,
        normalizedStep,
      );
      if (prev) {
        onStepChange(prev);
      }
      return;
    }
    if (!step) {
      return;
    }
    const prev = getStandardPrevOnboardingStep(step);
    if (prev) {
      onStepChange(prev);
    }
  }, [onStepChange, step, normalizedStep, ecomPlatformConfig]);

  const onComplete = useCallback(
    async (inviteUser?: boolean) => {
      if (!selectedProject) {
        return;
      }
      const res = await publishOnboarding(selectedProject.id as number);
      setOnboarding(res);
      if (
        !onboardingCompletedRegistered &&
        onboarding.onboardingType ===
          ApiOnboardingType.ONBOARDING_TYPE_WOO_COMMERCE
      ) {
        (window as any).inc("append_lead_status", ONBOARDING_COMPLETED);
        (window as any).inc("add_conversion", "start_trial");
        (window as any).inc("go");
        setOnboardingCompletedRegistered(true);
      }
      if (inviteUser) {
        inactiveUsers.forEach(async (user) => {
          const updateUser = {
            ...user,
            status: ApiStatus.ACTIVE,
          };

          const res = await saveUser(updateUser);

          setUsers((users) =>
            produce(users, (draft) => {
              const idx = draft.findIndex((d) => d.id === res.id);
              if (idx >= 0) {
                draft[idx] = res;
              } else {
                draft.push(res);
              }
            }),
          );

          allUsersData.setUsers((users) =>
            produce(users, (draft) => {
              const idx = draft.findIndex((d) => d.id === res.id);
              if (idx >= 0) {
                draft[idx] = res;
              } else {
                draft.push(res);
              }
            }),
          );
        });
      }
      history.push(
        `/clients/${selectedProject?.clientId}/projects/${selectedProject.id}`,
      );
    },
    [
      selectedProject,
      history,
      setOnboarding,
      inactiveUsers,
      allUsersData,
      setUsers,
      onboardingCompletedRegistered,
      onboarding.onboardingType,
    ],
  );

  const onNext = useCallback(
    (inviteUser?: boolean) => {
      if (ecomPlatformConfig && normalizedStep) {
        const next = getEcomPlatformNextOnboardingStep(
          ecomPlatformConfig,
          normalizedStep,
        );
        if (!next) {
          onStepChange(
            ApiOnboardingStep.CONNECTIONS,
            ApiOnboardingStep.CONNECTIONS,
          );
          onComplete(inviteUser);
          return;
        }
        onStepChange(next, normalizedStep);
        return;
      }
      switch (step) {
        case ApiOnboardingStep.BASIC_INFO:
          onStepChange(
            ApiOnboardingStep.BUSINESS_INFO,
            ApiOnboardingStep.BASIC_INFO,
          );
          break;
        case ApiOnboardingStep.BUSINESS_INFO:
          onStepChange(
            ApiOnboardingStep.PLATFORM,
            ApiOnboardingStep.BUSINESS_INFO,
          );
          break;
        case ApiOnboardingStep.PLATFORM:
          onStepChange(ApiOnboardingStep.PIXEL, ApiOnboardingStep.PLATFORM);
          break;
        case ApiOnboardingStep.PIXEL:
          onStepChange(ApiOnboardingStep.LOCATIONS, ApiOnboardingStep.PIXEL);
          break;
        case ApiOnboardingStep.LOCATIONS:
          onStepChange(
            ApiOnboardingStep.CONNECTIONS,
            ApiOnboardingStep.LOCATIONS,
          );
          break;
        case ApiOnboardingStep.CONNECTIONS:
          onStepChange(
            ApiOnboardingStep.CONNECTIONS,
            ApiOnboardingStep.CONNECTIONS,
          );
          onComplete(inviteUser);
          break;
      }
    },
    [onStepChange, step, ecomPlatformConfig, normalizedStep, onComplete],
  );

  const onLearningShow = useCallback(async () => {
    if (!hasPermission(ApiACLRole.SUPER_ADMIN)) {
      const res = await updateOnboarding(selectedProject!.id as number, {
        ...onboarding,
        learningShown: true,
      });
      setOnboarding(res);
    }
    setLearingShown(true);
  }, [selectedProject, hasPermission, onboarding, setOnboarding]);

  if (loading || !selectedProject || !selectedClient || !fetched) {
    return <Loading fullHeight />;
  }

  if (isEcomPlatformOnboarding && onboarding.onboardingType) {
    const continueLabel =
      normalizedStep === ApiOnboardingStep.CONNECTIONS
        ? hasPermission(ApiACLRole.SUPER_ADMIN)
          ? "Approve"
          : "Finish"
        : "Continue";

    return (
      <EcomPlatformOnboardingFlow
        onboardingType={onboarding.onboardingType}
        step={normalizedStep || defaultStep}
        onBack={onBack}
        onContinue={() => onNext()}
        onContinueWithInvite={
          hasPermission(ApiACLRole.SUPER_ADMIN) ? () => onNext(true) : undefined
        }
        continueLabel={continueLabel}
        inactiveUsers={inactiveUsers}
      />
    );
  }

  return (
    <StandardOnboardingFlow
      step={step || ApiOnboardingStep.BASIC_INFO}
      project={selectedProject}
      client={selectedClient}
      onBack={onBack}
      onNext={onNext}
      learningShown={Boolean(onboarding.learningShown || learingShown)}
      onLearningShow={onLearningShow}
      settingByType={settingByType}
      setSettings={setSettings}
      hasProjectType={hasProjectType}
      hasPermission={hasPermission}
      inactiveUsers={inactiveUsers}
      onStepChange={onStepChange}
    />
  );
}

export default GetStartedPage;
